import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Users, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const pct = (used, ent) => (ent > 0 ? Math.min(100, Math.round((used / ent) * 100)) : 0);

/** Personal annual-leave balance (all staff) + compact team table (admins). */
export function LeaveBalanceCard({ balance, pendingCount, isAdmin }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const { data: team } = useQuery({ queryKey: ["leave-balances", year], queryFn: () => api.leaveTeamBalances(year), enabled: isAdmin && /^\d{4}$/.test(year) });
  const ics = () => api.downloadHolidaysIcs(balance?.year || new Date().getFullYear()).catch(() => toast.error("Could not download the holiday calendar"));
  if (!balance) return null;
  const low = balance.remaining <= 2;
  return (
    <div className="grid gap-4 mb-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <div className="border border-border rounded-md bg-white px-5 py-4" data-testid="leave-balance">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="size-4 text-primary" />
          <span className="overline">My annual leave {balance.year}</span>
          {isAdmin && pendingCount > 0 && <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-400 text-amber-950" data-testid="leave-pending-count">{pendingCount} pending</span>}
        </div>
        <div className="flex items-end gap-2">
          <span className={`font-display text-4xl font-bold mono leading-none ${low ? "text-red-600" : ""}`} data-testid="leave-balance-remaining">{balance.remaining}</span>
          <span className="text-sm text-muted-foreground pb-1">of {balance.entitlement} days remaining</span>
        </div>
        <Progress value={pct(balance.used, balance.entitlement)} className="h-2 mt-3" data-testid="leave-balance-bar" />
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mt-2">
          <span data-testid="leave-balance-used">Taken: <b className="mono text-foreground">{balance.used}</b></span>
          <span data-testid="leave-balance-pending">Awaiting approval: <b className="mono text-foreground">{balance.pending}</b></span>
          <Button variant="link" size="sm" className="h-auto p-0 text-xs ml-auto" data-testid="leave-holidays-ics-btn" onClick={ics}><Download className="size-3 mr-1" /> Public holidays (.ics)</Button>
        </div>
      </div>

      {isAdmin && (
        <div className="border border-border rounded-md bg-white overflow-hidden" data-testid="leave-team-balances">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
            <Users className="size-4 text-primary" />
            <span className="overline">Team balances</span>
            <Input data-testid="leave-team-year" type="number" min="2000" max="2100" className="ml-auto h-7 w-24 mono text-xs" value={year} onChange={(e) => setYear(e.target.value)} />
          </div>
          {!team ? <p className="text-xs text-muted-foreground p-4">Loading…</p> : team.rows.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4" data-testid="leave-team-empty">No active employees in Payroll yet — balances appear once staff are added there.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">{["Employee", "Entitled", "Taken", "Pending", "Remaining"].map((h) => <TableHead key={h} className="overline h-8">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {team.rows.map((r) => (
                    <TableRow key={r.employee_id} data-testid={`leave-team-row-${r.employee_id}`}>
                      <TableCell className="py-1.5 text-sm font-medium">{r.name}<div className="text-[10px] text-muted-foreground">{r.position}</div></TableCell>
                      <TableCell className="py-1.5 mono text-xs">{r.entitlement}</TableCell>
                      <TableCell className="py-1.5 mono text-xs">{r.used}</TableCell>
                      <TableCell className="py-1.5 mono text-xs">{r.pending || "—"}</TableCell>
                      <TableCell className={`py-1.5 mono text-xs font-semibold ${r.remaining <= 2 ? "text-red-600" : ""}`}>{r.remaining}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
